import { useMemo, useRef, useState } from 'react';
import { X, CalendarCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';

interface Props {
  onClose: () => void;
  /**
   * Soumission. `quoteSentDate` (ISO YYYY-MM-DD) = date d'envoi réelle du
   * devis (toujours présente). `estimatedSigningDate` (ISO YYYY-MM-DD) n'est
   * fournie que lorsque `askSigningDate` est vrai (1er envoi de l'opp). La
   * promesse est awaitée pour gérer un toast / Banner d'erreur.
   */
  onSubmit: (data: { quoteSentDate: string; estimatedSigningDate?: string }) => Promise<void>;
  /**
   * `true` au 1er Envoyé de l'opp : on demande EN PLUS la date de signature
   * estimée (création de la tâche de relance). `false` aux envois suivants :
   * seul le champ « Date d'envoi » est demandé.
   */
  askSigningDate: boolean;
  /**
   * Nom du devis envoyé, affiché dans le header de la modale pour rappeler
   * au commercial le contexte. Optionnel.
   */
  quoteFileName?: string;
}

/**
 * Modale Stripe-like ouverte à chaque clic « Envoyer » sur un devis (refonte
 * 2026-05-22 / 2026-06-18). Demande :
 *  - **Date d'envoi du devis** (toujours) : peut être antérieure à aujourd'hui
 *    — le commercial a pu envoyer le devis vendredi et le renseigner lundi.
 *    Devient `quote.sent_at`. (Quand un service mail sera branché, cette date
 *    sera reprise automatiquement de l'envoi par mail et ce champ disparaîtra.)
 *  - **Date de signature estimée** (1er envoi de l'opp seulement) : crée la
 *    tâche « Signature estimée … » assignée au titulaire + persiste
 *    `opportunity.estimated_signing_date`.
 *
 * Pattern UI : focus trap + Escape + animate-fade-in/scale-in + fond
 * brand/30 backdrop-blur (identique à CloseLostModal). Gardes UI symétriques
 * au backend : date d'envoi <= aujourd'hui, signature estimée >= aujourd'hui.
 */
export function EstimatedSigningDateModal({
  onClose,
  onSubmit,
  askSigningDate,
  quoteFileName,
}: Props) {
  // Format YYYY-MM-DD du jour (UTC) pour alimenter les inputs date.
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [sentDate, setSentDate] = useState<string>(today);
  const [signingDate, setSigningDate] = useState<string>(today);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true });

  // Validation locale (le backend rejoue les gardes en 422 — on évite le
  // round-trip) : date d'envoi non vide et <= aujourd'hui ; date de signature
  // (si demandée) non vide et >= aujourd'hui.
  const isValid = useMemo(() => {
    if (!sentDate || sentDate > today) return false;
    if (askSigningDate && (!signingDate || signingDate < today)) return false;
    return true;
  }, [sentDate, signingDate, askSigningDate, today]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        quoteSentDate: sentDate,
        estimatedSigningDate: askSigningDate ? signingDate : undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Envoi du devis"
        onClick={(e) => e.stopPropagation()}
        className="shadow-modal w-full max-w-md rounded-lg bg-white animate-scale-in"
      >
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          <div className="border-border-subtle flex items-start justify-between border-b pb-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-600">
                  <CalendarCheck className="h-4 w-4" />
                </span>
                <h2 className="text-brand text-lg font-semibold">Envoi du devis au client</h2>
              </div>
              <p className="text-text-muted mt-1.5 text-xs">
                {quoteFileName ? (
                  <>
                    <span className="font-medium text-brand">{quoteFileName}</span> sera marqué
                    comme envoyé au client.
                  </>
                ) : (
                  <>Ce devis sera marqué comme envoyé au client.</>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-text-muted hover:text-brand ml-3 rounded-md p-1 hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div>
            <Label htmlFor="quoteSentDate" required>
              Date d&apos;envoi du devis
            </Label>
            <Input
              id="quoteSentDate"
              type="date"
              value={sentDate}
              onChange={(e) => setSentDate(e.target.value)}
              max={today}
              required
              autoFocus
            />
            <p className="text-text-muted mt-1.5 text-[11px]">
              Date à laquelle le devis a réellement été transmis au client. Peut être antérieure à
              aujourd&apos;hui (devis envoyé un autre jour, renseigné maintenant).
            </p>
          </div>

          {askSigningDate && (
            <div>
              <Label htmlFor="signingDate" required>
                Date de signature estimée
              </Label>
              <Input
                id="signingDate"
                type="date"
                value={signingDate}
                onChange={(e) => setSigningDate(e.target.value)}
                min={today}
                required
              />
              <p className="text-text-muted mt-1.5 text-[11px]">
                Une tâche de relance sera créée à cette date sur l&apos;opportunité, assignée au
                commercial titulaire. Auto-fermée si l&apos;opp est signée avant.
              </p>
            </div>
          )}

          {error && <Banner tone="danger">{error}</Banner>}

          <div className="border-border-subtle flex justify-end gap-2 border-t pt-4">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" loading={submitting} disabled={!isValid}>
              Confirmer l&apos;envoi
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
